/**
 * Dark mode depth: a user-level 0–100 preference that darkens the dark
 * palette by interpolating every dark surface variable in lockstep, so the
 * surface → hover → inset hierarchy stays calibrated at any depth.
 *
 * Depth 0 is the stock palette from globals.css; depth 100 is a near-black
 * variant. The preference lives in localStorage (like next-themes' `theme`)
 * and is applied as a `.dark {}` style override, so it never leaks into
 * light mode.
 *
 * A pre-hydration copy of this logic lives in _document.tsx so the chosen
 * depth paints on first load without a flash of the stock palette. Keep the
 * two in sync when changing anchors or the storage key.
 */

export const DARK_MODE_DEPTH_KEY = "dark-mode-depth";
export const DARK_MODE_DEPTH_DEFAULT = 0;
export const DARK_MODE_DEPTH_EVENT = "dark-mode-depth-change";
export const DARK_MODE_DEPTH_STYLE_ID = "dark-mode-depth-style";

export type PaletteAnchor = {
  surface: string;
  surfaceHover: string;
  surfaceInset: string;
  surfaceInsetHover: string;
  surfaceInsetActive: string;
  appBackground: string;
  border: string;
  sidebarBorderLightness: number;
};

// Depth 0 mirrors the `.dark` values in globals.css.
const AT_ZERO: PaletteAnchor = {
  surface: "#1d1e20",
  surfaceHover: "#252628",
  surfaceInset: "#242526",
  surfaceInsetHover: "#313235",
  surfaceInsetActive: "#3b3c3f",
  appBackground: "#18191a",
  border: "#39393b",
  sidebarBorderLightness: 15,
};

const AT_HUNDRED: PaletteAnchor = {
  surface: "#090a0c",
  surfaceHover: "#111214",
  surfaceInset: "#101112",
  surfaceInsetHover: "#1d1e21",
  surfaceInsetActive: "#27282b",
  appBackground: "#040506",
  border: "#252527",
  sidebarBorderLightness: 7,
};

function mixHex(from: string, to: string, t: number): string {
  const channel = (offset: number) => {
    const a = parseInt(from.slice(offset, offset + 2), 16);
    const b = parseInt(to.slice(offset, offset + 2), 16);
    return Math.round(a + (b - a) * t)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

export function clampDepth(value: number): number {
  if (!Number.isFinite(value)) return DARK_MODE_DEPTH_DEFAULT;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function darkPaletteAt(depth: number): PaletteAnchor {
  const t = clampDepth(depth) / 100;
  return {
    surface: mixHex(AT_ZERO.surface, AT_HUNDRED.surface, t),
    surfaceHover: mixHex(AT_ZERO.surfaceHover, AT_HUNDRED.surfaceHover, t),
    surfaceInset: mixHex(AT_ZERO.surfaceInset, AT_HUNDRED.surfaceInset, t),
    surfaceInsetHover: mixHex(
      AT_ZERO.surfaceInsetHover,
      AT_HUNDRED.surfaceInsetHover,
      t,
    ),
    surfaceInsetActive: mixHex(
      AT_ZERO.surfaceInsetActive,
      AT_HUNDRED.surfaceInsetActive,
      t,
    ),
    appBackground: mixHex(AT_ZERO.appBackground, AT_HUNDRED.appBackground, t),
    border: mixHex(AT_ZERO.border, AT_HUNDRED.border, t),
    sidebarBorderLightness: Math.round(
      AT_ZERO.sidebarBorderLightness +
        (AT_HUNDRED.sidebarBorderLightness - AT_ZERO.sidebarBorderLightness) *
          t,
    ),
  };
}

export function darkModeDepthCss(depth: number): string {
  const p = darkPaletteAt(depth);
  // Doubled class beats globals.css's single `.dark` rules regardless of
  // where this tag sits in <head>, so the pre-hydration copy (which lands
  // before the stylesheet <link>) wins on first paint with no flash.
  return [
    ".dark.dark {",
    `  --surface: ${p.surface};`,
    `  --surface-hover: ${p.surfaceHover};`,
    `  --surface-inset: ${p.surfaceInset};`,
    `  --surface-inset-hover: ${p.surfaceInsetHover};`,
    `  --surface-inset-active: ${p.surfaceInsetActive};`,
    `  --surface-transparent: ${p.surface}00;`,
    `  --app-background: ${p.appBackground};`,
    `  --app-background-transparent: ${p.appBackground}00;`,
    `  --border: ${p.border};`,
    `  --sidebar: ${p.surface};`,
    `  --sidebar-border: hsl(0, 0%, ${p.sidebarBorderLightness}%);`,
    "}",
  ].join("\n");
}

export function readStoredDepth(): number {
  if (typeof window === "undefined") return DARK_MODE_DEPTH_DEFAULT;
  const raw = window.localStorage.getItem(DARK_MODE_DEPTH_KEY);
  if (raw === null) return DARK_MODE_DEPTH_DEFAULT;
  return clampDepth(Number(raw));
}

export function applyDepth(depth: number): void {
  const clamped = clampDepth(depth);
  let style = document.getElementById(
    DARK_MODE_DEPTH_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (clamped === DARK_MODE_DEPTH_DEFAULT) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement("style");
    style.id = DARK_MODE_DEPTH_STYLE_ID;
  }
  style.textContent = darkModeDepthCss(clamped);
  document.head.appendChild(style);
}

export function storeDepth(depth: number): void {
  const clamped = clampDepth(depth);
  if (clamped === DARK_MODE_DEPTH_DEFAULT) {
    window.localStorage.removeItem(DARK_MODE_DEPTH_KEY);
  } else {
    window.localStorage.setItem(DARK_MODE_DEPTH_KEY, String(clamped));
  }
  applyDepth(clamped);
  window.dispatchEvent(
    new CustomEvent(DARK_MODE_DEPTH_EVENT, { detail: clamped }),
  );
}
